import type { ChannelProfile, OwnedStream, PublicStream, StreamCategory } from '@zip2live/shared';
import { viewerId } from './viewer-id.ts';

export interface LiveNow {
  streams: PublicStream[];
  categories: { id: StreamCategory; count: number }[];
  total: number;
}

/** A page of a channel's past streams (newest first). nextCursor fetches the next page. */
export interface ReplayPage {
  items: PublicStream[];
  nextCursor: string | null;
}

/** zip2live.com/<handle>: the profile, the live stream if any, and the first page of past streams. */
export interface ChannelPage extends ChannelProfile {
  /** People following the channel. */
  followers: number;
  live: PublicStream | null;
  /** The creator is looking at their own channel (they also see their private replays). */
  owner: boolean;
  replays: ReplayPage;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface NotifyFollowersState {
  notifyFollowers: boolean;
  followers: number;
  /** Set once the stream went live: 'expanding'/'enqueued' = being told, 'throttled' = told < 30 min ago, 'skipped' = off. */
  run: { status: string; reason: string | null; recipients: number } | null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

export const api = {
  createStream: (body: { title?: string; source?: 'browser' | 'obs' | 'mobile'; visibility?: 'public' | 'unlisted'; notifyFollowers?: boolean }, idempotencyKey: string) =>
    request<OwnedStream>('/streams', { method: 'POST', body: JSON.stringify(body), headers: { 'Idempotency-Key': idempotencyKey } }),
  getStream: (id: string) => request<PublicStream>(`/streams/${encodeURIComponent(id)}`),
  endStream: (id: string) => request<PublicStream>(`/streams/${encodeURIComponent(id)}/end`, { method: 'POST' }),
  /** Pick up a browser stream that is still on (closed tab, lost connection): same link, new publish key. */
  resumeStream: (id: string) => request<OwnedStream>(`/streams/${encodeURIComponent(id)}/resume`, { method: 'POST' }),
  setReplayVisibility: (id: string, visibility: 'unlisted' | 'private') =>
    request<PublicStream>(`/streams/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ visibility }) }),
  /** Public: listed on Live Now. Unlisted: anyone with the link. Either way, any time, even mid-stream. */
  setListed: (id: string, listed: boolean) =>
    request<PublicStream>(`/streams/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ visibility: listed ? 'public' : 'unlisted' }) }),
  vote: (id: string, value: 1 | -1 | 0) =>
    request<{ likes: number; mine: 1 | -1 | 0 }>(`/streams/${encodeURIComponent(id)}/vote?v=${viewerId()}`, { method: 'POST', body: JSON.stringify({ value }) }),
  liveNow: (category: StreamCategory | null, sort: 'trending' | 'viewers' | 'new') =>
    request<LiveNow>(`/live?sort=${sort}${category ? `&category=${category}` : ''}`),
  /** Retitle a stream (live or not); null or empty clears it. Open pages get it at once. */
  setTitle: (id: string, title: string | null) =>
    request<PublicStream>(`/streams/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  /** Title for the next stream, before it exists (OBS opens it by itself). */
  setNextTitle: (title: string | null) => request<{ title: string | null }>('/me/stream-title', { method: 'PUT', body: JSON.stringify({ title }) }),
  deleteStream: (id: string) => request<{ ok: true }>(`/streams/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  diagnostics: (id: string) => request<{ status: string; events: { type: string; created_at: number }[] }>(`/streams/${id}/diagnostics`),
  channel: (handle: string) => request<ChannelPage>(`/channels/${encodeURIComponent(handle)}`),
  channelReplays: (handle: string, cursor: string) => request<ReplayPage>(`/channels/${encodeURIComponent(handle)}/replays?cursor=${encodeURIComponent(cursor)}`),
  updateChannel: (body: { displayName?: string; bio?: string | null; links?: string[] }) =>
    request<Omit<ChannelProfile, 'handle'> & { handle: string | null }>('/me/channel', { method: 'PATCH', body: JSON.stringify(body) }),
  /** The cropped, resized photo (see lib/photo.ts); the API checks it before anyone sees it. */
  uploadPhoto: (photo: Blob) => request<{ photoUrl: string }>('/me/channel/photo', { method: 'PUT', body: photo, headers: { 'Content-Type': photo.type } }),
  removePhoto: () => request<{ photoUrl: null }>('/me/channel/photo', { method: 'DELETE' }),
  obs: () =>
    request<{
      server: string; streamKey: string | null; current: PublicStream | null; keyframeSeconds: number | null; chatOverlayUrl: string;
      nextTitle: string | null; notifyFollowers: boolean; followers: number;
    }>('/me/obs'),
  setObsNotifyFollowers: (notifyFollowers: boolean) => request<{ notifyFollowers: boolean }>('/me/obs', { method: 'PATCH', body: JSON.stringify({ notifyFollowers }) }),
  /** "Notify followers" for one broadcast, and what happened when it went live. */
  notifyFollowers: (id: string) => request<NotifyFollowersState>(`/streams/${encodeURIComponent(id)}/notify-followers`),
  setNotifyFollowers: (id: string, notifyFollowers: boolean) =>
    request<NotifyFollowersState>(`/streams/${encodeURIComponent(id)}/notify-followers`, { method: 'PATCH', body: JSON.stringify({ notifyFollowers }) }),
  channelOverlay: (channelId: string) => request<{ stream: PublicStream | null }>(`/channels/overlay/${encodeURIComponent(channelId)}`),
  obsKey: (rotate = false) => request<{ streamKey: string }>('/me/obs/key', { method: 'POST', body: JSON.stringify({ rotate }) }),
  me: () => request<import('./me.ts').Me>('/me'),
  checkHandle: (handle: string) => request<{ available: boolean; handle?: string; reason: string | null }>(`/channels/check-handle?handle=${encodeURIComponent(handle)}`),
  claimHandle: (handle: string) => request<{ handle: string }>('/channels/claim-handle', { method: 'POST', body: JSON.stringify({ handle }) }),
  setDisplayName: (displayName: string) => request('/me/channel', { method: 'PATCH', body: JSON.stringify({ displayName }) }),
  report: (streamId: string, reason: string, frameAt?: number | null) => request('/reports', { method: 'POST', body: JSON.stringify({ streamId, reason, frameAt: frameAt ?? undefined }) }),
};
